import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import { fetchMaterialChunks, MaterialChunk } from '../services/learningApi';

const PAGE_SIZE = 25;

interface Props {
  materialId: string;
  title: string;
  /** Extra actions (e.g. "Open in Learning") shown left of Close. */
  actions?: React.ReactNode;
  intro?: React.ReactNode;
  onClose: () => void;
}

/** The stored snippets of a vectorized material, exactly as searches and runs see them. */
export const MaterialChunksDialog: React.FC<Props> = ({ materialId, title, actions, intro, onClose }) => {
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [applied, setApplied] = useState('');
  const [data, setData] = useState<{ total: number; items: MaterialChunk[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await fetchMaterialChunks(() => tokenRef.current(), materialId, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, q: applied || undefined }),
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [materialId, page, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {intro || 'Each snippet below is stored and searched on its own. This is exactly the text a search or a run can receive from this file.'}
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter snippets by text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              setApplied(filter.trim());
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        {error && <Alert severity="error">{error}</Alert>}
        {loading && !data && <CircularProgress size={24} />}
        {data && (
          <>
            <Typography variant="caption" color="text.secondary">
              {data.total} snippet{data.total === 1 ? '' : 's'}
              {applied ? ` matching “${applied}”` : ''}
            </Typography>
            {data.total === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {applied ? 'No snippets contain that text.' : 'Nothing is stored for this file yet.'}
              </Typography>
            )}
            <Stack spacing={1} sx={{ mt: 1, opacity: loading ? 0.5 : 1 }}>
              {data.items.map((c) => (
                <Paper key={c.index} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={`#${c.index + 1}`} />
                    <Typography variant="caption" color="text.secondary">
                      {c.chars.toLocaleString()} chars
                    </Typography>
                    {c.metadata?.kind && <Chip size="small" variant="outlined" label={c.metadata.kind === 'fact' ? 'Fact' : 'Mapping'} />}
                    {c.metadata?.method === 'ai' && <Chip size="small" color="secondary" variant="outlined" label="AI-organized" />}
                    {Array.isArray(c.metadata?.pages) && c.metadata.pages.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {c.metadata.pages.length === 1
                          ? `p. ${c.metadata.pages[0]}`
                          : `pp. ${Math.min(...c.metadata.pages)}–${Math.max(...c.metadata.pages)}`}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {c.content}
                  </Typography>
                </Paper>
              ))}
            </Stack>
            {pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size="small" />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {actions}
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
